<template>
  <general-loader v-if="loading" loading/>
  <TabLayout v-else-if="!loading && loadTable" class="sale-view" :tabs="tabs">
    <template v-slot:title>
      <artisio-breadcrumb :items="breadcrumbs"/>
      <span class="ml-3" v-as-status="sale.status">{{
          sale.status | removeUnderscores | toUpperCaseFirst | translate
        }}</span>
      <span class="ml-3 bg-warning badge border text-white" v-if="sale.is_paused">{{ $t('Paused') }}</span>
    </template>
    <template v-slot:buttons>
      <b-button v-permission="'change_auction'" variant="primary" @click="onCreateClick" class="mr-2">
        <font-awesome-icon :icon="'plus-circle'" class="icon"/>
        {{ $t('New Sale') }}
      </b-button>

      <b-dropdown class="mr-2" right v-permission="['OR', 'change_auction', 'auction_results_auction']"
                  :text="$t('Auction Providers')"
                  v-if="liveAuctionProvider && liveAuctionProvider.length > 0">

        <template v-if="!isCompleted">
          <b-dropdown-item v-for="lp in liveAuctionProvider.filter(flp => flp.actions.includes('sync'))"
                           :key="`sync-with-btn-${lp.provider_key}`" v-permission="'change_auction'"
                           @click="onSyncToLiveClick(lp.class_name)">
            {{ $t('Sync with') }} {{ lp.provider_name }}
          </b-dropdown-item>
        </template>

        <template v-if="!isCompleted">
          <b-dropdown-item v-for="lp in liveAuctionProvider.filter(flp => flp.actions.includes('pull_results'))"
                           :key="`pull-results-btn-${lp.provider_key}`" v-permission="'auction_results_auction'"
                           @click="onSyncToLiveClick(lp.class_name,'pull_results')">
            {{ $t('Pull Results from') }} {{ lp.provider_name }}
          </b-dropdown-item>
        </template>

        <template v-if="!isCompleted && enableInventoryDraft">
          <b-dropdown-item v-for="lp in liveAuctionProvider.filter(flp => flp.actions.includes('publish_draft_lots'))"
                           v-permission="'change_auction'" :key="`publish-draft-btn-${lp.provider_key}`"
                           @click="onSyncToLiveClick(lp.class_name,'publish_draft_lots')">
            {{ $t('Publish Draft Lots') }} {{ lp.provider_name }}
          </b-dropdown-item>
        </template>

        <b-dropdown-item v-for="lp in liveAuctionProvider.filter(flp => flp.actions.includes('import_results')) "
                         v-permission="'auction_results_auction'" :key="`import-btn-${lp.provider_key}`"
                         :disabled="syncingSale === sale.uuid" @click="onImportResultsClick(lp.class_name)">
          {{ $t('Import') }} {{ lp.provider_name }} {{ $t('Results') }}
        </b-dropdown-item>

        <b-dropdown-item v-for="lp in liveAuctionProvider.filter(flp => flp.actions.includes('export_data')) "
                         v-permission="'change_auction'" :key="`import-btn-${lp.provider_key}_export`"
                         :disabled="syncingSale === sale.uuid"
                         @click="onExportDataClick(lp.provider_key, 'export_data')">
          {{ $t('Export') }} {{ lp.provider_name }} {{ $t('Sale') }}
        </b-dropdown-item>

        <input :multiple="true" @change="onSaleResultsInputChange"
               accept=".csv,.xls,.xlsx" type="file" class="d-none"
               ref="saleResultsInput"
               id="sale-results-input"/>

        <template v-if="liveAuctionProvider && liveAuctionProvider.length > 0">
          <b-dd-divider/>
          <b-dropdown-item v-permission="'auction_results_auction'"
                           @click="onAuctionResultsSyncClick">
            {{ $t('Auction Results Sync') }}
          </b-dropdown-item>
        </template>

      </b-dropdown>

      <b-dropdown v-if="$hasPermission('change_auction') ||
                        $hasPermission('auction_console') ||
                        ($hasPermission('pause_auction') && isTimed && isPublished)"
                  right :text="$t('More')">
        <b-dropdown-item @click="onEditClick" v-permission="'change_auction'">
          {{ $t('Edit Sale') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'change_auction'" @click="onSyncWithAccountingSystemClick"
                         v-if="isAccountingEnabled && !isManualAccountingSyncDisabled">
          {{ $t('Sync with Accounting System') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'publish_auction'" @click="onPublishClick" v-if="isDraft">
          {{ $t('Publish') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'change_auction'" @click="onArchiveClick" v-if="isCompleted">
          {{ $t('Archive') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'change_auction'" @click="onRevertToCompletedClick" v-if="isArchived">
          {{ $t('Revert to Completed') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'change_auction'" @click="onMarkAsCompletedClick"
                         v-if="(!isTimed || !hasArtisioTimedProvider || (artisioTimedProviderInfo && artisioTimedProviderInfo.catalogue_only_auctions)) && isPublished">
          {{ $t('Mark As Completed') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'pause_auction'" @click="onPauseClick"
                         v-if="isTimed && isPublished && !sale.is_paused">
          {{ $t('Pause Auction') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'pause_auction'" @click="onResumeClick"
                         v-if="isTimed && isPublished && sale.is_paused">
          {{ $t('Resume Auction') }}
        </b-dropdown-item>
        <template v-if="!isTimed">
          <b-dd-divider v-permission="'change_auction'"/>
          <b-dropdown-item v-permission="'auction_console'" @click="onStartSaleClick">
            {{ $t('Start Auction Console') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'change_auction'" target="_blank"
                           :to="{name: 'auctioneerConsole', params: {saleNo: sale.sale_no}, query: {type: 'sale'}}">
            {{ $t('Open Auctioneer View') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'change_auction'"
                           @click="onAuctionFileExportClick('auction_auctioneers_book')">
            {{ $t("Export Auctioneer's Book") }}
          </b-dropdown-item>
        </template>
        <b-dd-divider v-permission="'change_auction'"/>
        <b-dropdown-item v-permission="'change_auction'" @click="onViewCatalogClick">
          {{ $t('View Catalog') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'change_auction'" @click="onRegenerateCatalogClick">
          {{ $t('Regenerate Catalog') }}
        </b-dropdown-item>
        <b-dropdown-item v-if="sale.website_url" @click="copyWebsiteUrl">
          {{ $t('Copy Website URL') }}
        </b-dropdown-item>
        <b-dd-divider v-permission="'change_auction'"/>
        <b-dropdown-item @click="generateOrRetrieveAuctioneersSheet">
          {{ $t("Auctioneer's Sheet") }}
        </b-dropdown-item>
        <b-dropdown-item :disabled="sale.auctioneers_sheet_status !== 'Locked'"
                         @click="generateOrRetrieveAfterSaleAuctioneersSheet">
          {{ $t("After Sale Auctioneer's Sheet") }}
        </b-dropdown-item>
        <b-dd-divider/>
        <b-dropdown-item v-permission="'change_auction'" @click="onAuctionFileExportClick('csv')">
          {{ $t('Export CSV') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'change_auction'" @click="onAuctionFileExportClick('pdf')">
          {{ $t('Export PDF') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'change_auction'" @click="onAuctionFileExportClick('xlsx')">
          {{ $t('Export Excel') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'change_auction'" @click="onAuctionFileExportClick('auction_sale_profile_pdf')">
          {{ $t('Export Sale Profile') }}
        </b-dropdown-item>
      </b-dropdown>

    </template>
    <sale-modal/>
    <sale-resume-modal/>
    <sale-pause-modal/>
    <sale-export-modal @onSubmit="onSaleExportModalSubmit"/>
  </TabLayout>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import SaleModal from "../SaleModal";
  import {SOURCE_SALES_VIEW, WS_ACCOUNTING_SYNC_PROGRESS} from "../../constants";
  import {ON_AUCTIONEERS_SHEET_GENERATED, ON_AUCTIONEERS_SHEET_LOCKED} from "@/plugins/event-bus/event-bus-names";
  import ArtisioBreadcrumb from "../../../core/components/artisio-breadcrumb/ArtisioBreadcrumb";
  import TabLayout from "../../../core/components/layout/DefaultLayout/TabLayout";
  import LiveBidsService from "@/modules/live-bids/LiveBidsService";
  import GeneralLoader from "@/core/components/general-loader/GeneralLoader";
  import SalesService from "@/modules/sales/SalesService";
  import AccountingService from "@/modules/setup/accounting/AccountingService";
  import cloneDeep from "lodash/cloneDeep";
  import SaleResumeModal from "@/modules/sales/SaleResumeModal.vue";
  import SalePauseModal from "@/modules/sales/SalePauseModal.vue";
  import i18n from "@/shared/i18n";
  import uuidV4 from 'uuid';
  import SaleExportModal from "@/modules/sales/view/modals/SaleExportModal";

  const {mapActions, mapState, mapMutations} = createNamespacedHelpers('sales');
  const {mapState: lotsMapState, mapActions: lotMapAction} = createNamespacedHelpers('lots');
  const {mapActions: mapActionsGlobal, mapState: mapStateGlobal} = createNamespacedHelpers('global');
  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  export default {
    name: "SalesView",
    components: {
      SaleExportModal,
      SalePauseModal, SaleResumeModal, GeneralLoader, TabLayout, ArtisioBreadcrumb, SaleModal
    },
    data: () => {
      return {
        breadcrumbs: [],
        syncProgressValue: null,
        itemType: null,
        syncingSale: null,
        progressUuid: null,
        loadTable: true,
        processIdToProgressMap: {},
        currentLiveProviderClassName: null
      };
    },
    computed: {
      ...lotsMapState({
        lotsSearchModel: state => state.tableData[state.tableSource] ? state.tableData[state.tableSource].searchModel : null
      }),
      ...mapState({
        loading: state => state.view.loading,
        sale: state => state.view.currentSale
      }),
      ...mapState(['saleSyncProgresses', 'saleSyncProcessIdToProgressMap']),
      ...mapStateGlobal({
        liveAuctionProvider: state => state.liveAuctionProvider,
        liveAuctionProviderName: state => state.liveAuctionProviderName,
        currentUser: state => state.currentUser,
        isAccountingEnabled: state => state.isAccountingEnabled,
        isManualAccountingSyncDisabled: state => state.isManualAccountingSyncDisabled,
        enableContinuousAuction: state => state.enableContinuousAuction,
        enableInventoryDraft: state => state.enableInventoryDraft,
        enableSepa: state => state.enableSepa,
        hasArtisioTimedProvider: state => state.hasArtisioTimedProvider,
        batchUnsoldParams: state => state.batchUnsoldParams,
        artisioTimedProviderInfo: state => state.artisioTimedProviderInfo
      }),
      isTimed() {
        return this.sale.type === 'timed'
      },
      isDraft() {
        return this.sale.status === 'draft';
      },
      isCompleted() {
        return this.sale.status === 'completed';
      },
      isArchived() {
        return this.sale.status === 'archived';
      },
      isPublished() {
        return this.sale.status === 'published';
      },
      tabs() {
        let tabs = [
          {title: 'Dashboard', icon: 'server', to: {name: 'sales.dashboard'}},
          {title: 'Lots', icon: 'boxes', to: {name: 'sales.lots'}, viewPermission: 'view_inventoryitem'},
          {title: 'Bidders', icon: 'user-friends', to: {name: 'sales.bidders'}, viewPermission: 'view_bidder'},
          {title: 'Bids', icon: 'user-friends', to: {name: 'sales.bids'}, viewPermission: 'view_bid'},
          // {title: 'Telephone Bids', icon: 'phone-alt', to: {name: 'sales.telephone-bids'}, viewPermission: 'view_telephonebid'},
          {
            title: this.sale.type === 'live' ? 'Comm. & Tel. Bids' : 'Max Bids',
            icon: 'gavel',
            to: {name: 'sales.pre-bids'},
            viewPermission: 'view_prebid'
          },
          {title: 'Sale Settings', icon: 'percentage', to: {name: 'sales.terms'}},
          {
            title: 'Condition Reports Requests',
            icon: 'file-alt',
            to: {name: 'sales.condition-report-requests'},
            viewPermission: 'view_conditionreportrequest'
          },
          {title: 'Invoices', icon: 'file-invoice', to: {name: 'sales.invoices'}, viewPermission: 'view_invoice'},
          {title: 'Vendors', icon: 'user-tie', to: {name: 'sales.vendors'}, viewPermission: 'view_settlement'},
          {
            title: 'Settlements',
            icon: 'file-invoice',
            to: {name: 'sales.settlements'},
            viewPermission: 'view_settlement'
          },
          {title: 'DPV', icon: 'file', to: {name: 'sales.dpv'}},
          {
            title: 'SEPA',
            icon: 'file-invoice',
            to: {name: 'sales.sepa'},
            viewPermission: 'change_auction'
          },
          {
            title: 'Attachments',
            icon: 'paperclip',
            to: {
              name: 'sales.attachments'
            }
          },
          {title: 'Notes', icon: 'comment-alt', to: {name: 'sales.notes'}},
          {title: 'Collection Appointments', icon: 'file', to: {name: 'sales.collection-appointments'}},
          {title: 'Activities', icon: 'stream', to: {name: 'sales.activities'}},
        ];

        if (!this.batchUnsoldParams.enable_dpv) {
          tabs = tabs.filter(t => t.to.name !== 'sales.dpv');
        }

        if (!this.enableSepa) {
          tabs = tabs.filter(t => t.to.name !== 'sales.sepa')
        }

        return tabs
      }
    },
    watch: {
      async '$route.params.id'() {
        await this.getSale(this.$route.params.id);
        this.initBreadcrumbs();
      },
      saleSyncProgresses() {
        if (this.saleSyncProgresses) {
          this.onSyncWithTimedAuctionsDataReceive(this.saleSyncProgresses)
        }
      },
    },
    methods: {
      ...mapActions(['getSale', 'showModal', 'syncToLive', 'exportAuctionProviderData', 'updateStatus', 'showSalePauseModal', 'showSaleResumeModal', 'showSaleExportModal']),
      ...lotMapAction(['showAuctionResultsSyncModal']),
      ...mapActionsGlobal(['addProgress', 'removeProgress', 'updateProgressValue']),
      ...mapMutations(['addSaleSyncProcessId', 'deleteSaleSyncProcessId', 'deleteSaleSyncProgress']),
      onEditClick() {
        this.showModal({
          object: this.sale,
          source: SOURCE_SALES_VIEW
        });
      },
      onCreateClick() {
        this.showModal();
      },
      onAuctionResultsSyncClick() {
        this.showAuctionResultsSyncModal({
          source: SOURCE_SALES_VIEW,
          sale: cloneDeep(this.sale)
        })
      },
      async onStartSaleClick() {
        let response = await LiveBidsService.saleExists(this.sale.sale_no);

        if (response.success) {
          this.$router.push({name: 'liveBids', params: {saleNo: this.sale.sale_no}})
        } else {
          this.$errorDialog(response.body.sale_no ? response.body.sale_no[0] : this.$i18n.t('Something went wrong'));
        }
      },
      initBreadcrumbs() {
        this.breadcrumbs = [
          {
            text: this.$i18n.t('Sales'),
            to: {name: 'sales'},
            icon: 'server'
          },
          {
            text: `Sale ${this.sale.sale_no}`,
          }
        ];
      },
      async onExportDataClick(providerClassName, action) {

        let response = await this.exportAuctionProviderData({
          sale_no: this.sale.sale_no,
          action,
          provider_name: providerClassName
        });
        if (response.success) {
          this.$toast('success', this.$i18n.t("Downloading of sale data has successfully started"))
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.toastError) {
            this.$errorDialog(response.toastError);
          }
        }
      },

      onImportResultsClick(providerClassName) {
        this.currentLiveProviderClassName = providerClassName
        this.$refs.saleResultsInput.click()
      },
      async onSyncToLiveClick(provider_name, action = null) {
        let response = await this.syncToLive({sale_no: this.sale.sale_no, action, provider_name});
        if (response.success) {
          this.$toast('success', this.$i18n.t('Sale synchronization has successfully started'))
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.toastError) {
            this.$errorDialog(response.toastError);
          }
        }
      },
      async statusChange(status) {
        const params = {
          auction_uuids: [this.sale.uuid],
        };
        let response = await this.updateStatus({status: status, params});
        if (response.success) {
          this.$toast('success', this.$i18n.t('Sale published'))
          const success = await this.getSale(this.sale.uuid);
          this.initBreadcrumbs();

          if (!success) {
            await this.$errorDialog(this.$i18n.t('Something went wrong'));
          }
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.toastError) {
            this.$errorDialog(response.toastError);
          }
        }
      },
      async onPublishClick() {
        await this.statusChange('publish');
      },
      async onArchiveClick() {
        await this.statusChange('archive');
      },
      async onRevertToCompletedClick() {
        await this.statusChange('revert-to-completed');
      },
      async onPauseClick() {
        // If auction hasn't yet started
        if (!this.sale.start_date || (new Date(this.sale.start_date).getTime() >= new Date().getTime())) {
          this.$toast('danger', this.$i18n.t(`You can't pause auction which has not yet started.`))
          return;
        }
        this.showSalePauseModal();
      },
      onResumeClick() {
        this.showSaleResumeModal();
      },
      async onMarkAsCompletedClick() {
        let success = await this.$confirm(
          this.$i18n.t(`You are about to mark this auction as completed. After this action you will no longer be able to sync this sale with ${this.liveAuctionProviderName} and if required you will have to manually make the corrections in auction results. This action is irreversible`),
          null,
          null,
          null,
          this.$i18n.t('Please Confirm'),
          this.$i18n.t('Yes'),
          this.$i18n.t('No'),
          null,
          {
            label: this.$i18n.t("To proceed please type 'completed' below and click Submit"),
            text: 'completed'
          }
        )
        if (!success) {
          return;
        }

        await this.statusChange('completed');
      },
      async oldTimedAuctionsDataReceiveLogic(data) {
        this.itemType = this.$options.filters.removeUnderscores(this.$options.filters.toUpperCaseFirst(data.item_type));
        this.syncingSale = data.auction_id;
        if (this.syncingSale === this.sale.uuid) {
          if (data.total === 0) {
            this.syncProgressValue = null
          }
          if (data.status === 'fetching') {
            this.syncProgressValue = 0
          } else if (data.status === 'processing') {
            this.syncProgressValue = parseInt(Math.round(((data.successful + data.failed) / data.total) * 100))
          } else if (data.status === 'finished') {
            this.syncProgressValue = null;
          }

          if (data.is_completed) {
            if (!data.errors) {
              this.$toast('success', this.$i18n.t('Sale synchronization successfully finished'))
            }
            this.getSale(this.sale.uuid);
            this.syncProgressValue = null;
            this.syncingSale = null;
            this.removeProgress(this.progressUuid);
            this.progressUuid = null;
          } else {
            let progress = null;
            const name = this.$i18n.t("Syncing {item_type}", {item_type: this.itemType});

            if (!this.progressUuid) {
              progress = await this.addProgress(name);
              this.progressUuid = progress.uuid;
            }

            let obj = {
              name,
              progressValue: this.syncProgressValue,
              uuid: this.progressUuid
            }
            this.updateProgressValue(obj)
          }

          if (data.errors) {
            let errorData = [];
            Object.keys(data.errors).forEach(key => {
              errorData.push(data.errors[key])
            })
            const listItemsArr = [];
            errorData.forEach(err => err.map(e => (listItemsArr.push(this.$createElement('li', {}, e)))))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr));
            this.syncProgressValue = null;
          }
        }
      },
      async newTimedAuctionsDataReceiveLogic(saleSyncProcessIdToProgressMap) {
        for (let id in saleSyncProcessIdToProgressMap) {
          const process = saleSyncProcessIdToProgressMap[id]
          const isResultsImportProcess = this.isResultsImportAction(process.actions);
          const isExportAuctionProcess = this.isExportAuction(process.actions);
          let progress = this.saleSyncProcessIdToProgressMap[process.id];

          if (process.status === 'finished') {
            if (progress) {
              this.removeProgress(progress.uuid);
            }
            this.deleteSaleSyncProcessId(process.id)
            this.deleteSaleSyncProgress(process.id);

            if (process.errors && Object.keys(process.errors).length > 0) {
              if (this.currentUser.uuid === process.created_by_uuid) {
                this.handleErrors(process)
              }
            } else {
              if (isExportAuctionProcess) {
                let exportMessage = this.$i18n.t(`Export auction ${process.sale_no} for provider is ready`)
                let title = this.$i18n.t("Provider export");
                this.showExportProviderButton(process, title, exportMessage)
              } else {
                let message = isResultsImportProcess ? 'Sale results import completed for sale {sale_no}'
                  : 'Sale synchronization successfully finished for sale {sale_no}';
                this.$toast('success', this.$i18n.t(message, {sale_no: process.sale_no}))
              }
            }
          } else {
            if (!progress) {
              let message = isResultsImportProcess ? "Sale results import for sale {sale_no}, Started by {created_by} at {created_at}"
                : "Synchronizing sale {sale_no}, Started by {created_by} at {created_at}";
              if (isExportAuctionProcess) {
                message = `Exporting sale ${process.sale_no}, Started by ${process.created_by} at ${this.$options.filters.toDateTime(process.created_at)}`;
              }
              progress = await this.addProgress(this.$i18n.t(message, {
                sale_no: process.sale_no,
                created_by: process.created_by,
                created_at: this.$options.filters.toDateTime(process.created_at),
              }))
            }
            progress = {
              ...progress,
              progressValue: parseInt(Math.round((process.finished / process.total) * 100))
            }
            this.updateProgressValue(progress);
            this.addSaleSyncProcessId({processId: process.id, progress});
          }
        }
      },
      isResultsImportAction(data) {
        if (!data) {
          return false;
        }
        return (data.length === 1 && data[0].action === "import_results");
      },
      isExportAuction(data) {
        if (!data) {
          return false;
        }
        return (data.length === 1 && data[0].action === "export_data");
      },
      async onSyncWithTimedAuctionsDataReceive(data) {
        await this.newTimedAuctionsDataReceiveLogic(data);
      },
      handleErrors(process) {
        let errors = process.errors;
        let errorData = [];
        Object.keys(errors).forEach(key => {
          errorData.push(errors[key])
        })
        const listItemsArr = [];
        errorData.forEach(err => err.map(e => (listItemsArr.push(this.$createElement('li', {}, e)))))
        this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t("Error for sale {sale_no}", {sale_no: process.sale_no}));
      },
      showExportProviderButton(data, title, message) {
        const h = this.$createElement
        const vNodesMsg = h('p', {class: ['mb-0']}, [message])
        const id = uuidV4()
        const hasCsvUrl = data.actions[0].data['url']
        if (hasCsvUrl) {
          const buttons = h(
            'div',
            {class: ['mt-2']},
            [
              h(
                'b-button',
                {
                  props: {variant: 'success',},
                  on: {
                    click: () => {
                      this.$bvToast.hide(id);
                      window.open(hasCsvUrl, '_blank')
                    }
                  }
                },
                i18n.t('Download')
              ),
              h(
                'b-button',
                {
                  class: ['ml-2'],
                  props: {variant: 'secondary',},
                  on: {
                    click: () => {
                      this.$bvToast.hide(id);
                    }
                  }
                },
                i18n.t('Dismiss')
              ),
            ]
          )

          this.$bvToast.toast([vNodesMsg, buttons], {
            id: id,
            title: title,
            variant: 'info',
            toaster: 'b-toaster-top-center',
            noAutoHide: true
          })
        }
      },

      async onSaleResultsInputChange($event) {
        const files = $event.target.files;
        if (files.length === 0) {
          return false;
        }
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
          formData.append('file[]', files[i]);
        }
        formData.append('provider_name', this.currentLiveProviderClassName);
        let response = await SalesService.uploadSaleResults(this.sale.sale_no, formData);
        if (response.success) {
          this.$toast('success', this.$i18n.t('Sale results import task successfully added to the queue.'));
          await this.getSale(this.sale.uuid);
        } else {
          if (response.body.non_field_errors) {
            this.displayErrorList(response.body.non_field_errors, 'Sale Results Validation');
          } else if (response.toastError) {
            this.$errorDialog(response.toastError)
          }
        }
        this.$refs.saleResultsInput.value = null;
        this.currentLiveProviderClassName = null
      },
      displayErrorList(errorList, title) {
        const listItemsArr = [];
        errorList.forEach(err => (listItemsArr.push(this.$createElement('li', {}, err))));
        this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), title);
      },
      handleAccountingErrors(process) {
        let errorData = [];
        for (let item of process.errors) {
          Object.keys(item).forEach(invoiceNo => {
            errorData.push(this.$i18n.t("Invoice: {invoiceNo}. Error: {error}", {
              invoiceNo: invoiceNo,
              error: item[invoiceNo]
            }))
          })
        }
        const listItemsArr = [];
        errorData.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
        this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t("Error for sale {sale_no}", {sale_no: process.sale_no}));
      },
      async onSyncWithAccountingSystemDataReceive(data) {
        for (let process of data) {
          let progress = this.processIdToProgressMap[process.id];
          if (process.status === 'finished') {
            if (progress) {
              this.removeProgress(progress.uuid);
              delete this.processIdToProgressMap[process.id]
            }
            if (process.errors && Object.keys(process.errors).length > 0) {
              if (this.currentUser.uuid === process.created_by_uuid) {
                this.handleAccountingErrors(process)
              }
            } else {
              this.$toast('success', this.$i18n.t('Sale synchronization with accounting system successfully finished for sale {sale_no}', {sale_no: process.sale_no}))
            }
          } else {
            if (!progress) {
              progress = await this.addProgress(this.$i18n.t("Synchronizing with accounting system for sale {sale_no}, Started by {created_by} at {created_at}", {
                sale_no: process.sale_no,
                created_by: process.created_by,
                created_at: this.$options.filters.toDateTime(process.created_at),
              }))
            }
            progress = {
              ...progress,
              progressValue: parseInt(Math.round(((process.success_count + process.failed_count) / process.invoice_count) * 100))
            }
            this.updateProgressValue(progress);
            this.processIdToProgressMap[process.id] = progress;
          }
        }

      },
      async onSyncWithAccountingSystemClick() {
        let response = await AccountingService.syncWithAccountingSystem({sale_no: this.sale.sale_no});
        if (response.success) {
          this.$toast('success', this.$i18n.t('Sale synchronization with accounting system has successfully started'))
        } else if (response.body.non_field_error) {
          this.$errorDialog(response.body.non_field_error[0]);
        }
      },
      hideSaleSyncProgressBars() {
        for (let id in this.saleSyncProgresses) {
          const process = this.saleSyncProgresses[id]
          let progress = this.saleSyncProcessIdToProgressMap[process.id];
          if (progress) {
            this.removeProgress(progress.uuid);
            this.deleteSaleSyncProcessId(process.id)
          }
        }
      },
      onViewCatalogClick() {
        SalesService.viewAuctionCatalog(this, this.sale.uuid, this.sale.sale_no, false, this.getLotTablePayload())
      },
      onRegenerateCatalogClick() {
        SalesService.viewAuctionCatalog(this, this.sale.uuid, this.sale.sale_no, true, this.getLotTablePayload())
      },
      copyWebsiteUrl() {
        const tempInput = document.createElement('input');
        tempInput.value = this.sale.website_url;
        document.body.appendChild(tempInput);

        // Select the text in the temporary input
        tempInput.select();
        document.execCommand('copy');

        // Remove the temporary input from the DOM
        document.body.removeChild(tempInput);
      },
      getLotTablePayload() {
        let payload = {}
        if (this.lotsSearchModel) {
          payload = this.lotsSearchModel.toJSON();
          for (let key in payload) {
            if (!payload[key]) {
              delete payload[key]
            }
          }
        }
        return payload
      },
      async onSaleExportModalSubmit({include_field_with_html_tags, language_code, file_type, sale}) {
        let payload = this.getLotTablePayload();
        payload.auction_uuid = sale.uuid
        payload.type = file_type
        payload.language_code = language_code
        payload.include_field_with_html_tags = include_field_with_html_tags
        let response = await SalesService.exportSaleFile(this.sale.uuid, payload)
        if (response.success) {
          if (file_type === 'xlsx') {
            this.$toast('success', this.$i18n.t("Auction Excel export generation has successfully started"))
          }
        } else {
          if (response.body.non_field_errors) {
            this.$toast('info', response.body.non_field_errors[0])
          } else {
            this.$toast('danger', this.$i18n.t("Something went wrong. Please, try again later"))
          }
        }
      },
      async onAuctionFileExportClick(type) {
        if (type === 'xlsx') {
          this.showSaleExportModal({sale: this.sale, fileType: type})
          return
        }
        let payload = this.getLotTablePayload();
        payload.auction_uuid = this.sale.uuid
        payload.type = type
        let response = await SalesService.exportSaleFile(this.sale.uuid, payload)
        if (response.success) {
          if (type === 'csv') {
            this.$toast('success', this.$i18n.t("Auction CSV export generation has successfully started"))
          } else if (type === 'xlsx') {
            this.$toast('success', this.$i18n.t("Auction Excel export generation has successfully started"))
          } else if (type === 'pdf') {
            this.$toast('success', this.$i18n.t("Auction PDF export generation has successfully started"))
          } else if (type === 'auction_sale_profile_pdf') {
            this.$toast('success', this.$i18n.t("Auction sale profile export generation has successfully started"))
          } else if (type === 'auction_auctioneers_book') {
            this.$toast('success', this.$i18n.t("Auctioneer's book export generation has successfully started"))
          }
        } else {
          if (response.body.non_field_errors) {
            this.$toast('info', response.body.non_field_errors[0])
          } else {
            this.$toast('danger', this.$i18n.t("Something went wrong. Please, try again later"))
          }
        }
      },
      generateOrRetrieveAuctioneersSheet() {
        SalesService.generateOrRetrieveAuctioneersSheet(this, this.sale.uuid, true);
      },
      generateOrRetrieveAfterSaleAuctioneersSheet() {
        SalesService.generateOrRetrieveAfterSaleAuctioneersSheet(this, this.sale.uuid, true);
      },
      updateAuctioneersSheetStatus(data) {
        if (data.report_type == 'after_sale_auctioneers_sheet') {
          if (this.sale && this.sale.after_sale_auctioneers_sheet_status === 'N/A') {
            this.sale.after_sale_auctioneers_sheet_status = 'Draft';
          }
        } else {
          if (this.sale && this.sale.auctioneers_sheet_status === 'N/A') {
            this.sale.auctioneers_sheet_status = 'Draft';
          }
        }
      },
      updateAuctioneersSheetLockedStatus(data) {
        if (data.reportType === 1) {
          if (this.sale) {
            this.sale.auctioneers_sheet_status = data.statusText;
          }
        }
        if (data.reportType === 2) {
          if (this.sale) {
            this.sale.after_sale_auctioneers_sheet_status = data.statusText;
          }
        }
      }
    },
    mounted() {
      if (this.saleSyncProgresses) {
        this.onSyncWithTimedAuctionsDataReceive(this.saleSyncProgresses)
      }

      this.$ws.listen(`auctions.${WS_ACCOUNTING_SYNC_PROGRESS}`, this.onSyncWithAccountingSystemDataReceive);
    },
    async beforeMount() {
      if (this.enableContinuousAuction) {
        this.loadTable = false;
        await this.$router.push({name: 'not-found'});
      } else {
        const success = await this.getSale(this.$route.params.id);
        if (!success) {
          await this.$errorDialog(this.$i18n.t('Something went wrong'));
          this.$generalGoBack(this)
        } else {
          this.initBreadcrumbs();
        }
      }
      this.$evBus.$on(ON_AUCTIONEERS_SHEET_GENERATED, this.updateAuctioneersSheetStatus);
      this.$evBus.$on(ON_AUCTIONEERS_SHEET_LOCKED, this.updateAuctioneersSheetLockedStatus);
    },
    beforeDestroy() {
      this.$ws.remove(`auctions.${WS_ACCOUNTING_SYNC_PROGRESS}`);
      this.$evBus.$off(ON_AUCTIONEERS_SHEET_GENERATED)
      this.$evBus.$off(ON_AUCTIONEERS_SHEET_LOCKED)
      this.hideSaleSyncProgressBars();
    }
  }

</script>

<style scoped lang="scss">
  @import "./../scss/variables";

  .sale-view {
    @include view-page-wrapper;
  }
</style>
